import axios from 'axios';
import React, { useEffect, useState, useCallback } from 'react';
import { Gauge, Factory } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { API } from '../../config';

const TICK_SM = { fontSize: 11 };
const TICK_XS = { fontSize: 10 };
const BAR_RADIUS = [3, 3, 0, 0];

const fmtMt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const pctColor = (p) => {
  if (p >= 90) return '#10b981';
  if (p >= 70) return '#3b82f6';
  if (p >= 50) return '#f59e0b';
  return '#ef4444';
};

const UtilizationGauge = ({ pct, label }) => {
  const clamped = Math.max(0, Math.min(150, pct || 0));
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-32 h-32">
        <svg className="w-32 h-32 transform -rotate-90">
          <circle cx="64" cy="64" r="56" stroke="#e5e7eb" strokeWidth="12" fill="none" />
          <circle
            cx="64" cy="64" r="56" stroke={pctColor(clamped)} strokeWidth="12" fill="none"
            strokeDasharray={`${(clamped / 100) * 351.86} 351.86`}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{clamped.toFixed(1)}%</div>
            <div className="text-xs text-gray-500">{label}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PlantCard = ({ row }) => (
  <div className="bg-white rounded-lg shadow-md p-5" data-testid={`utilization-plant-${row.plant_id}`}>
    <div className="flex items-start justify-between mb-3">
      <div>
        <h3 className="text-lg font-bold text-gray-900 flex items-center">
          <Factory className="h-5 w-5 mr-2 text-[#156ACA]" />
          {row.plant_name}
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">FY{row.fy_year} · Apr 1 → today ({row.elapsed_days}/{row.fy_total_days} days)</p>
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4 mb-4">
      <UtilizationGauge pct={row.utilization_pct} label="Pro-rated" />
      <div className="sm:col-span-2 space-y-2 self-center">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Production YTD</span>
          <strong>{fmtMt(row.production_mt_to_date)} MT</strong>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Expected by today</span>
          <strong>{fmtMt(row.expected_mt_to_date)} MT</strong>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Annual Capacity</span>
          <strong>{fmtMt(row.annual_capacity_mt)} MT</strong>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Full-year tracker</span>
          <strong>{row.full_year_utilization_pct?.toFixed(1)}%</strong>
        </div>
      </div>
    </div>

    {row.monthly && row.monthly.some(m => m.production_mt > 0) && (
      <div className="mt-2">
        <p className="text-xs font-semibold text-gray-600 mb-1">Monthly utilization (% of capacity / 12)</p>
        <div style={{ width: '100%', height: 140 }}>
          <ResponsiveContainer>
            <BarChart data={row.monthly}>
              <XAxis dataKey="month_label" tick={TICK_SM} />
              <YAxis tick={TICK_SM} unit="%" />
              <Tooltip formatter={(v, _name, p) => [`${Number(v).toFixed(1)}% (${fmtMt(p.payload.production_mt)} MT)`, 'Utilization']} />
              <Bar dataKey="utilization_pct" radius={BAR_RADIUS}>
                {row.monthly.map((m) => (
                  <Cell key={m.month_label} fill={pctColor(m.utilization_pct)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    )}
  </div>
);

const UtilizationView = ({ dashboardPlant, selectedYear, token }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const plantId = dashboardPlant?.id || 'all';

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/utilization/${plantId}?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load utilization');
      setData(null);
    } finally { setLoading(false); }
  }, [plantId, selectedYear, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#156ACA]" /></div>;
  if (error) return <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center text-gray-700">{error}</div>;
  if (!data) return null;

  const rows = plantId === 'all' ? data.plants : [data];

  return (
    <div className="space-y-4" data-testid="utilization-view">
      {plantId === 'all' && data.consolidated && (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-5 border-l-4 border-[#156ACA]" data-testid="utilization-consolidated">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                <Gauge className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-[#156ACA] flex-shrink-0" />
                Consolidated Utilization — FY{data.fy_year}
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">All accessible plants combined</p>
            </div>
            <div className="flex items-center space-x-4 sm:space-x-6">
              <UtilizationGauge pct={data.consolidated.utilization_pct} label="Pro-rated" />
              <div className="text-xs sm:text-sm space-y-1 min-w-0">
                <div><strong>{fmtMt(data.consolidated.production_mt_to_date)} MT</strong> produced</div>
                <div className="text-gray-500">of <strong>{fmtMt(data.consolidated.expected_mt_to_date)} MT</strong> expected</div>
                <div className="text-gray-500">capacity: {fmtMt(data.consolidated.annual_capacity_mt)} MT/yr</div>
                <div className="text-gray-500">full-year: {data.consolidated.full_year_utilization_pct?.toFixed(1)}%</div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {rows.map(r => <PlantCard key={r.plant_id} row={r} />)}
      </div>
    </div>
  );
};

export default UtilizationView;
